import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link } from '@compat/router';
import { observer } from 'mobx-react';
import { Picture } from '@ui';
import { twcls } from '@utils/twcls';
import icons from '@icons';
import { oysterBarIcon } from '@images';
import { useStores } from '@root/src/stores';
import type { CatalogMenuItem } from '@store/CatalogStore.type';

const {
  pricetag: { midYellow: pricetagMidYellowFilled },
  fire: { red: fireRedFilled },
  flash: { red: flashRedFilled }
} = icons.Fill;
const {
  percent: { black: percentBlackOutlined },
  chevronRight: { lightGrey: chevronRightLightGreyOutlined },
  chevronLeft: { red: chevronLeftRedOutlined }
} = icons.Outline;

const quickLinks = [
  {
    id: 'newbee',
    title: 'Новинки',
    href: '/catalog/newbee',
    icon: flashRedFilled
  },
  { id: 0, title: 'Акции', href: '/promotions', icon: percentBlackOutlined },
  { id: 1, title: 'Скидки', href: '/catalog/promo', icon: fireRedFilled },
  {
    id: 2,
    title: 'Распродажа',
    href: '/catalog/rasprodazha',
    icon: pricetagMidYellowFilled
  }
];

const rowClassName = twcls(`
  w-full
  h-14
  flex
  flex-row
  items-center
  rounded-xl
  bg-white
  transition-shadow
  hover:shadow-main-light
  hover:text-main-dark-red
`);

interface MobileCatalogViewProps {
  isOpen?: boolean;
  onNavigate?: () => void;
  scrollWindow?: boolean;
}

const scrollOpenListToTop = (
  node: HTMLElement | null,
  scrollWindow: boolean
): void => {
  if (scrollWindow) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  let parent = node?.parentElement ?? null;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      parent.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    parent = parent.parentElement;
  }
};

const MobileCatalogView: FC<MobileCatalogViewProps> = observer(
  ({ isOpen = true, onNavigate, scrollWindow = false }) => {
    const { catalogStore, cartStore } = useStores();
    const rootRef = useRef<HTMLDivElement>(null);
    const [selectedCategory, setSelectedCategory] =
      useState<CatalogMenuItem | null>(null);

    const menu = catalogStore.menu.filter(item => {
      if (item.isBarMenu && !cartStore.isCurrentAddressValidForExpress) {
        return false;
      }
      return true;
    });
    const selectedChildren = selectedCategory?.childrenItems || [];

    useEffect(() => {
      if (!isOpen) {
        setSelectedCategory(null);
      }
    }, [isOpen]);

    useEffect(() => {
      if (!selectedCategory) {
        return;
      }
      scrollOpenListToTop(rootRef.current, scrollWindow);
    }, [scrollWindow, selectedCategory]);

    return (
      <div ref={rootRef}>
        {selectedCategory ? (
          <h1 className='flex items-center mb-4 text-lg font-medium'>
            <button
              type='button'
              className='flex items-center mr-2 bg-transparent'
              onClick={() => {
                setSelectedCategory(null);
              }}
              aria-label='Назад'
            >
              <Picture
                icon={chevronLeftRedOutlined}
                width='24'
                height='24'
                alt=''
              />
            </button>
            Категории
          </h1>
        ) : (
          <h1 className='block mb-4 text-lg font-medium'>Каталог товаров</h1>
        )}

        {selectedCategory ? null : (
          <div className='flex items-center max-w-full mb-4 space-x-3 overflow-auto pb-2'>
            {quickLinks.map(item => (
              <Link
                key={item.id}
                className='w-full flex items-center justify-center px-5 py-3 bg-white rounded-xl transition-shadow hover:shadow-main-light hover:text-main-dark-red'
                to={item.href}
                onClick={onNavigate}
              >
                <Picture
                  pictureClassName='mr-2'
                  icon={item.icon}
                  height='24'
                  width='24'
                />
                {item.title}
              </Link>
            ))}
          </div>
        )}

        <div className='grid w-full grid-cols-1 auto-rows-[3.5rem] mb-10 gap-2 items-start'>
          {selectedCategory
            ? selectedChildren.map(item => (
                <Link
                  key={item.identify || item.id || item.title}
                  className={twcls(rowClassName, 'px-3')}
                  to={item.url}
                  onClick={onNavigate}
                >
                  <span className='min-w-0 flex-1'>{item.title}</span>
                </Link>
              ))
            : menu.map(item => (
                <div
                  key={item.id}
                  className={twcls(
                    rowClassName,
                    'pl-3',
                    !item.childrenItems?.length && 'pr-3'
                  )}
                >
                  <Link
                    className='flex items-center flex-1 min-w-0 h-full py-2'
                    to={item.url}
                    onClick={onNavigate}
                  >
                    <Picture
                      icon={item.icon}
                      width='40'
                      height='40'
                      alt={item.title || 'Изображение'}
                      pictureClassName='mr-4 shrink-0'
                    />
                    <span className='min-w-0 flex-1'>{item.title}</span>
                  </Link>
                  {!!item.childrenItems?.length && (
                    <button
                      type='button'
                      className='flex items-center justify-center shrink-0 w-12 h-full bg-transparent'
                      aria-label='Подкатегории'
                      onClick={() => {
                        setSelectedCategory(item);
                      }}
                    >
                      <Picture
                        icon={chevronRightLightGreyOutlined}
                        width='24'
                        height='24'
                        alt=''
                      />
                    </button>
                  )}
                </div>
              ))}
          {selectedCategory ? null : (
            <Link
              className={rowClassName}
              style={{ backgroundColor: '#DEF4FF' }}
              to='/catering-ustrichniy-bar'
              onClick={onNavigate}
            >
              <span className='flex items-center flex-1 min-w-0 h-full py-2 pl-3'>
                <Picture
                  icon={oysterBarIcon}
                  width='40'
                  height='40'
                  alt='Выездной устричный бар'
                  pictureClassName='mr-4 shrink-0'
                />
                <span className='min-w-0 flex-1'>Выездной устричный бар</span>
              </span>
              <span className='flex items-center justify-center shrink-0 w-12 h-full'>
                <Picture
                  icon={chevronRightLightGreyOutlined}
                  width='24'
                  height='24'
                  alt=''
                />
              </span>
            </Link>
          )}
        </div>
      </div>
    );
  }
);

export default MobileCatalogView;
